<template>
  <div v-if="showMicPermissionRequest">
    <MicPermissionRequest
      @close="showMicPermissionRequest = false"
      @granted="showMicPermissionRequest = false"
    />
  </div>
  <div class="voice-recorder-container">
    <button
      v-if="state === 'initial'"
      @click="startRecording"
      :disabled="disableButton"
      class="recorder-button initial"
      :aria-label="$t('audioRecorder.start')"
    >
      <font-awesome-icon icon="microphone" class="mic-icon" />
    </button>

    <!-- Recording state with cancel and send options. The held state reuses
         this pill unchanged apart from the label and the hidden bars: the
         learner still has exactly the same two choices, send or discard. -->
    <div
      v-else-if="state === 'recording' || state === 'held'"
      class="recording-controls"
    >
      <!-- Cancel button -->
      <button
        @click="showCancelConfirmation"
        class="control-button cancel-button"
        :aria-label="$t('audioRecorder.cancel')"
      >
        <font-awesome-icon icon="xmark" />
      </button>

      <!-- Recording indicator with volume bars and timer -->
      <div class="recording-indicator">
        <!-- Hidden once the recording is held: the meter has nothing left to
             show, and the label needs the width it was taking. -->
        <div
          v-if="state !== 'held'"
          class="volume-bars"
          :class="{ 'volume-quiet': showQuietWarning }"
        >
          <span
            v-for="n in 5"
            :key="n"
            class="volume-bar"
            :style="{ height: getBarHeight(n) + 'px' }"
          ></span>
        </div>
        <!-- aria-hidden on both the timer and the label: a screen reader must
             not read a new string every second. The live region below carries
             the same information as a handful of discrete announcements. -->
        <span class="recording-timer" aria-hidden="true">{{ formattedTimer }}</span>
        <span
          class="recording-text"
          :class="{
            'recording-text-warning': showQuietWarning || showCountdown,
            'recording-text-roomy': state === 'held' || showCountdown,
          }"
          aria-hidden="true"
        >
          <template v-if="state === 'held'">{{
            holdReason === 'limit'
              ? $t('audioRecorder.limitReached')
              : $t('audioRecorder.stopped')
          }}</template>
          <template v-else-if="showQuietWarning">{{
            $t('audioRecorder.speakLouder')
          }}</template>
          <template v-else-if="showCountdown">{{
            $t('audioRecorder.secondsLeft', { count: secondsRemaining })
          }}</template>
          <template v-else>{{ $t('audioRecorder.recording') }}</template>
        </span>
      </div>

      <!-- Send button -->
      <button
        @click="sendRecording"
        class="control-button send-button"
        :aria-label="$t('audioRecorder.send')"
      >
        <font-awesome-icon icon="arrow-up" />
      </button>
    </div>

    <!-- The recorder's only announcement channel. Kept mounted for the whole
         life of the component rather than inside the pill above, because a
         live region that appears with its text already set is not reliably
         announced. It receives a small number of discrete strings, never the
         per-second timer. -->
    <span class="visually-hidden" role="status" aria-live="polite">{{
      liveAnnouncement
    }}</span>

    <!-- Cancel Confirmation Modal. Dismissing it (backdrop, Esc, drag-down)
         keeps the recording going, matching the "keep recording" action. -->
    <Modal
      v-model="showCancelModal"
      hide-header
      hide-footer
      :aria-label="$t('audioRecorder.cancelTitle')"
      :dialog-class="'modal-dialog-centered confirm-dialog'"
    >
      <div class="confirm-sheet">
        <div class="confirm-icon danger">
          <font-awesome-icon icon="trash-can" />
        </div>
        <h3 class="confirm-title">{{ $t('audioRecorder.cancelTitle') }}</h3>
        <p class="confirm-text">{{ $t('audioRecorder.cancelMessage') }}</p>
        <div class="confirm-actions">
          <!-- "Keep recording" would be a lie in the held state: nothing is
               recording any more, and the choice is only whether to keep what
               was captured. -->
          <button @click="hideCancelModal" class="confirm-btn primary">
            {{ state === 'held' ? $t('audioRecorder.keep') : $t('audioRecorder.keepRecording') }}
          </button>
          <button @click="cancelRecording" class="confirm-btn destructive">
            {{ $t('audioRecorder.discard') }}
          </button>
        </div>
      </div>
    </Modal>

    <!-- Quiet Recording Confirmation Modal. Static on purpose: it parks the
         only copy of the captured audio, so a backdrop tap, Esc or the
         Android back button must not be able to discard it. The learner
         chooses "Re-record" or "Send anyway". -->
    <Modal
      v-model="showQuietModal"
      :backdrop="'static'"
      hide-header
      hide-footer
      :aria-label="$t('audioRecorder.quietTitle')"
      :dialog-class="'modal-dialog-centered confirm-dialog'"
    >
      <div class="confirm-sheet">
        <div class="confirm-icon warning">
          <font-awesome-icon icon="triangle-exclamation" />
        </div>
        <h3 class="confirm-title">{{ $t('audioRecorder.quietTitle') }}</h3>
        <p class="confirm-text">{{ $t('audioRecorder.quietMessage') }}</p>
        <div class="confirm-actions">
          <button @click="discardQuietRecording" class="confirm-btn primary">
            {{ $t('audioRecorder.reRecord') }}
          </button>
          <button @click="confirmSendQuiet" class="confirm-btn secondary">
            {{ $t('audioRecorder.sendAnyway') }}
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import MicPermissionRequest from "./MicPermissionRequest.vue";
import Modal from "./Modal.vue";
import { useHaptics } from "@/composables/useHaptics.js";
import { useKeepAwake } from "@/composables/useKeepAwake.js";
import { useRecordingEvents } from "@/composables/useRecordingEvents.js";
import { useToast } from "@/composables/useToast.js";
import { pause as pauseSharedAudio } from "@/scripts/audioPlaybackService.js";
import { featureFlags } from "@/store/featureFlags.js";
import { setTakeInProgress } from "@/store/recordingState.js";

// Whisper accepts: mp3, mp4, mpeg, mpga, m4a, wav, webm. We pick the first
// type the browser actually supports. iOS Safari/WKWebView produces audio/mp4
// (AAC); Chrome/Firefox produce audio/webm (Opus).
const PREFERRED_MIME_TYPES = [
  "audio/mp4",
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
];

const QUIET_THRESHOLD = 0.05;
const QUIET_ALERT_MS = 3000;
const MIN_BLOB_SIZE_BYTES = 1500;
// Fraction of frames below QUIET_THRESHOLD that we treat as "no real audio"
// captured. 0.95 catches recordings that were silent the whole time (broken
// mic) without false-positiving short pauses in real speech.
const SILENT_RATIO_FAIL = 0.95;
const SILENT_RATIO_WARN = 0.8;

// The longest a single recording may run, in seconds, on every surface.
// Exported so the tests reference the constant rather than the literal: the
// number is expected to be retuned once real round-trip timings from a device
// exist, and a retune must move the tests with it rather than break them.
export const MAX_RECORDING_SECONDS = 90;
// How long before the ceiling the pill starts counting down.
export const COUNTDOWN_SECONDS = 20;
// The bound on an upload, in bytes, shared with continueChat.MAX_UPLOAD_BYTES.
// A real 90 second clip is between 0.4 and 1.5 MB, so this is several times
// the headroom any legitimate capture needs; it exists to stop a malformed
// capture from being uploaded, not to make a product decision.
export const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;
// What the server's copy of that bound measures is the WHOLE multipart
// envelope: the boundary delimiters, each part's MIME headers, and the small
// `json` form field ChatScreen sends alongside the audio. Only the audio is
// measured here, so the client's check is given an allowance for the rest.
// Without it a blob a few hundred bytes under the bound would pass here and
// then be refused with a 413, which is the same failed send but arrives more
// slowly and after the whole body has been uploaded. The allowance is far
// larger than any real envelope so the client is always the first refusal.
const MULTIPART_ENVELOPE_ALLOWANCE = 64 * 1024;
export const MAX_BLOB_BYTES = MAX_UPLOAD_BYTES - MULTIPART_ENVELOPE_ALLOWANCE;

// How long a muted microphone track has to stay muted before the recording is
// treated as interrupted. `mute` is transient by definition and is paired with
// `unmute`: a Bluetooth route change (an AirPod taken out or put back, a
// notification sound on Android, a USB headset hot-plug) mutes the track for a
// moment and then restores it, and the iOS audio session is deliberately
// configured for exactly those devices. A route change that resolves inside
// this window therefore costs nothing, while an audio-session interruption —
// an incoming call, or Siri — mutes for as long as it lasts and is held.
// Exported so the tests reference the constant rather than the literal.
export const MUTE_HOLD_MS = 2000;

function pickMimeType() {
  if (typeof MediaRecorder === "undefined") return "";
  for (const mime of PREFERRED_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return "";
}

function extensionFor(mimeType) {
  if (!mimeType) return "webm";
  if (mimeType.startsWith("audio/mp4")) return "m4a";
  if (mimeType.startsWith("audio/webm")) return "webm";
  if (mimeType.startsWith("audio/ogg")) return "ogg";
  return "webm";
}

export default {
  name: "AudioRecorder",
  components: { MicPermissionRequest, Modal },
  props: {
    disableButton: Boolean,
  },
  emits: ["new-recording"],
  data() {
    return {
      state: "initial", // 'initial' | 'recording' | 'held'
      hasMicAccess: false,
      showMicPermissionRequest: false,
      showCancelModal: false,
      isSending: false,
      volumeLevel: 0,
      showQuietWarning: false,
      showQuietModal: false,
      pendingPayload: null,
      recordingSeconds: 0,
      timerInterval: null,
      // Why the recording is being held, once it is: 'limit' when the ceiling
      // was reached, 'stopped' when capture was interrupted. Decides the
      // label and the announcement, and is null in every other state.
      holdReason: null,
      // The ceiling's backstop timer. The clock check on every tick is the
      // primary enforcement; this fires the hold on time when the interval is
      // late. Its handle lives here so stopVolumeMonitor can clear it and a
      // finished recording cannot fire the ceiling into a later one.
      ceilingTimeout: null,
      // MAX_RECORDING_SECONDS, copied at recording start. A running ceiling
      // must not be able to move under the recording it is bounding.
      maxRecordingSeconds: MAX_RECORDING_SECONDS,
      // True from the moment the recording is held until the learner sends or
      // discards it, so the outcome event is emitted exactly once and only
      // for a recording that was actually held.
      heldOutcomePending: false,
      // Whether the countdown has already been announced for this recording.
      // The visible label changes every second; the announcement is made once.
      countdownAnnounced: false,
      // The only text the screen-reader live region ever carries.
      liveAnnouncement: "",
      // Bumped every time the region is emptied. announce() empties the
      // region and refills it a tick later, and this is what lets that
      // deferred refill tell whether it is still the current announcement.
      announcementGeneration: 0,
      // True from the moment recorder.start() succeeds until
      // finalizeCapture runs. Every automatic stop checks it, and
      // finalizeCapture is the only thing that clears it.
      captureLive: false,
      // The in-flight finalizeCapture promise, and the result it stashed.
      // Both are cleared at the start of every recording and on every
      // terminal outcome; see finalizeCapture.
      finalizePromise: null,
      capturedPayload: null,
      // recordingScreenAwake, copied at tap time. The store is reactive to
      // live RTDB listeners, so an admin flipping the flag off mid-recording
      // must not change what this recording does. The wake-lock RELEASE
      // never consults this.
      screenAwakeArmed: false,
      // Set by beforeUnmount. startRecording awaits the microphone before
      // it arms anything, and the component can be unmounted during that
      // await, so the continuation has to know its instance is gone.
      isUnmounted: false,
      // True only while startRecording is awaiting the microphone. The
      // start button stays mounted for that whole window (state is still
      // 'initial'), so without this a double tap runs the method twice and
      // the second run overwrites this.stream, this.recorder, this.
      // timerInterval and this.monitorAudioContext, orphaning the first
      // recording's microphone track, encoder, interval and audio graph
      // with no reference left to stop any of them.
      isStarting: false,
      // Bumped by clearCapture. A send that is awaiting the finalize step
      // compares it afterwards, so a discard that landed in the meantime
      // cannot be overtaken by the send it interrupted.
      captureGeneration: 0,
      // --- interruption watch (all four are torn down in stopVolumeMonitor) --
      // The `visibilitychange` handler while one is registered, null otherwise.
      // Registered on native only, so on the web it stays null and a desktop
      // tab switch cannot reach the hold.
      visibilityHandler: null,
      // The microphone track the ended/mute/unmute listeners are attached to.
      // Held so they can be removed from the same object they were added to,
      // BEFORE releaseStream stops it — otherwise the recorder's own teardown
      // would fire its own interruption signals.
      interruptionTrack: null,
      // The @capacitor/app appStateChange registration handle.
      appStateListener: null,
      // Bumped by every startInterruptionWatch. The @capacitor/app import is
      // asynchronous, so a registration can land after the recording that asked
      // for it is over; the continuation compares this before keeping it.
      interruptionGeneration: 0,
      // True between startInterruptionWatch and stopInterruptionWatch, which is
      // the other half of the same check: a registration that lands after the
      // watch was torn down is removed immediately rather than kept.
      interruptionWatchArmed: false,
      // The mute window's timer handle, per the MUTE_HOLD_MS comment above. A
      // second `mute` while one window is open must not open another, and
      // stopVolumeMonitor clears it so a window left open past the end of a
      // recording cannot fire into the next one.
      muteHoldTimeout: null,
      // When that window opened, read from both clocks, or null when no window
      // is open. The timer alone is not enough to decide whether a mute was
      // transient: a mobile browser freezes JavaScript while the tab is hidden
      // or the screen is locked, so a track can stay muted for a minute with
      // the timeout never running, and the `unmute` delivered on resume would
      // cancel it. Neither clock alone is enough either, which is why there are
      // two. performance.now() is monotonic, so a clock correction cannot move
      // it, but whether it advances across an OS suspension is
      // platform-specific (spec Part 1b says exactly this), and a suspension is
      // one of the cases this window exists to catch. Date.now() does advance
      // across a suspension but a correction can move it in either direction.
      // handleTrackUnmute takes the LONGER of the two readings, so a frozen
      // monotonic clock is covered by the wall clock, and a wall clock that
      // jumped backwards is simply ignored.
      muteStartedAtMonotonic: null,
      muteStartedAtWall: null,
      // True from the moment a hold starts until it settles. captureLive is
      // cleared synchronously by finalizeCapture and `state` does not become
      // 'held' until the collect resolves, so for the length of that collect
      // neither of them says there is a recording worth protecting. The
      // Android back handler consults this so a press in that window is still
      // a two-step discard rather than a silent navigation away from audio the
      // hold is in the middle of rescuing.
      holdPending: false,
      // Counted by the volume-meter loop; the silent-stream guard in
      // sendRecording reads them. Initialize here (not in startVolumeMonitor)
      // so they're never undefined if the monitor fails to start — otherwise
      // the guard's `totalFrames >= 30` check silently evaluates to false and
      // a silent recording slips through.
      totalFrames: 0,
      quietFrames: 0,
      // Identifies this recorder in the shared recording store, so that
      // unmounting one recorder host clears only its own take.
      recordingHost: Symbol("AudioRecorder"),
    };
  },
  watch: {
    // Published to the shared recording store the moment it changes, not on
    // the next render: a phrase tap in the same tick as the record tap must
    // already see the take. See src/store/recordingState.js.
    takeInProgress: {
      handler(active) {
        setTakeInProgress(this.recordingHost, active);
      },
      flush: "sync",
    },
    // Defensive no-op. The modal is now static, so backdrop taps, Esc, the
    // drag-down gesture and the Android back button cannot close it, and
    // both explicit outcomes null pendingPayload before they close it.
    // Nothing can reach the branch below any more; it stays as a backstop
    // in case the modal ever loses its static backdrop, because an implicit
    // discard here would throw away audio the learner never chose to lose.
    showQuietModal(isOpen) {
      if (!isOpen && this.pendingPayload) {
        this.discardQuietRecording();
      }
    },
    // Announced once, when the countdown begins, rather than on every tick:
    // the visible label counts down every second and the region must not.
    showCountdown(isCountingDown) {
      if (!isCountingDown || this.countdownAnnounced) return;
      this.countdownAnnounced = true;
      this.announce(
        this.$t("audioRecorder.secondsLeft", { count: this.secondsRemaining })
      );
    },
    // The quiet warning is set and cleared by the volume meter, which runs on
    // every animation frame, so the announcement is tied to the transition
    // into the warning rather than to the flag being true.
    showQuietWarning(isQuiet) {
      if (isQuiet) this.announce(this.$t("audioRecorder.speakLouder"));
    },
  },
  computed: {
    // A take is in progress from the record tap, while the microphone is
    // still being asked for, until the recording is sent, discarded or
    // abandoned. The held state counts, because the learner has not finished
    // with the recording yet and may resume it.
    takeInProgress() {
      return this.isStarting || this.state !== "initial";
    },
    formattedTimer() {
      const m = Math.floor(this.recordingSeconds / 60).toString().padStart(2, "0");
      const s = (this.recordingSeconds % 60).toString().padStart(2, "0");
      return `${m}:${s}`;
    },
    secondsRemaining() {
      return Math.max(0, this.maxRecordingSeconds - this.recordingSeconds);
    },
    // Only a recording that is actually bounded gets a countdown. With the
    // flag off there is no ceiling, so counting down to one would promise a
    // stop that never comes.
    showCountdown() {
      return (
        this.screenAwakeArmed &&
        this.state === "recording" &&
        this.secondsRemaining <= COUNTDOWN_SECONDS
      );
    },
  },
  methods: {
    async startRecording() {
      pauseSharedAudio();
      // Read the flag once, here, and copy it onto the instance for the
      // life of this recording. A startup read would be wrong (flags start
      // false and can flip true moments after load) and a live read would
      // let an admin change the rules mid-recording.
      if (this.isStarting) return;
      this.screenAwakeArmed = featureFlags.recordingScreenAwake === true;
      this.clearCapture();

      if (typeof MediaRecorder === "undefined") {
        useToast().showToast(this.$t("audioRecorder.recordingFailed"), "error");
        return;
      }

      let stream;
      this.isStarting = true;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (error) {
        console.error("getUserMedia failed:", error);
        // Both are permission denials the recovery modal can guide out of; a
        // native WebView sometimes reports the denial as SecurityError.
        if (error.name === "NotAllowedError" || error.name === "SecurityError") {
          this.showMicPermissionRequest = true;
        } else {
          useToast().showToast(this.$t("audioRecorder.recordingFailed"), "error");
        }
        return;
      } finally {
        // Everything after this await is synchronous, so the await IS the
        // whole re-entrancy window.
        this.isStarting = false;
      }

      // The permission prompt can sit in front of the learner while they
      // navigate away. Everything below arms a live microphone, a repeating
      // timer and a screen wake lock on an instance whose teardown has
      // already run, and nothing would ever release them: beforeUnmount has
      // been and gone. Releasing the wake lock is the sharp end, because on
      // Android a flag left set pins the screen on every later resume.
      if (this.isUnmounted) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      const mimeType = pickMimeType();
      let recorder;
      try {
        recorder = mimeType
          ? new MediaRecorder(stream, { mimeType })
          : new MediaRecorder(stream);
      } catch (error) {
        console.error("MediaRecorder construction failed:", error);
        stream.getTracks().forEach((t) => t.stop());
        useToast().showToast(this.$t("audioRecorder.recordingFailed"), "error");
        return;
      }

      this.stream = stream;
      this.recorder = recorder;
      this.recordedChunks = [];
      this.recorder.addEventListener("dataavailable", (e) => {
        if (e.data && e.data.size > 0) this.recordedChunks.push(e.data);
      });
      // MediaRecorder.onerror fires for hardware/encoder failures we'd
      // otherwise miss (mic disconnected, encoder crash). Surface them.
      this.recorder.addEventListener("error", (e) => {
        console.error("MediaRecorder error:", e.error || e);
      });

      try {
        // Ask for a 1s timeslice so dataavailable fires periodically; some
        // WebKit builds ignore this and only emit one chunk on stop, in which
        // case the worst-case behaviour matches the unparameterized default.
        this.recorder.start(1000);
      } catch (error) {
        console.error("MediaRecorder.start failed:", error);
        stream.getTracks().forEach((t) => t.stop());
        this.recorder = null;
        this.stream = null;
        useToast().showToast(this.$t("audioRecorder.recordingFailed"), "error");
        return;
      }

      this.state = "recording";
      this.captureLive = true;
      this.holdReason = null;
      this.countdownAnnounced = false;
      if (this._hapticMedium) this._hapticMedium();
      this.recordingSeconds = 0;
      this.recordingStartedAt = performance.now();
      // Copied onto the instance so the ceiling this recording is judged
      // against cannot move while it runs.
      this.maxRecordingSeconds = MAX_RECORDING_SECONDS;
      this.timerInterval = setInterval(this.updateElapsedSeconds, 1000);
      // Backstop for the clock check in updateElapsedSeconds. Both call the
      // same guarded hold, so whichever arrives first wins and the other is a
      // no-op. Armed only when the flag is on, because the ceiling exists to
      // bound a wake lock that only the flag takes.
      if (this.screenAwakeArmed) {
        this.ceilingTimeout = setTimeout(
          this.holdAtCeiling,
          this.maxRecordingSeconds * 1000
        );
      }
      this.announce(this.$t("audioRecorder.recording"));
      // Reset before the audio graph is built, not inside
      // startVolumeMonitor: if the graph fails to build on a retry (iOS
      // single-mic-session contention is a documented cause) the send gates
      // would otherwise evaluate the PREVIOUS recording's counters.
      this.quietFrames = 0;
      this.totalFrames = 0;
      this.startVolumeMonitor(stream);
      if (this.screenAwakeArmed) {
        this.startInterruptionWatch(stream);
        this.armScreenAwake();
      }
    },
    /**
     * Recompute the displayed elapsed time from the clock. Separate from the
     * interval callback below so a hold can call it without re-entering the
     * ceiling check that callback performs.
     */
    refreshElapsedSeconds() {
      this.recordingSeconds = Math.max(
        0,
        Math.floor((performance.now() - this.recordingStartedAt) / 1000)
      );
    },
    // The displayed timer reads a clock rather than counting callbacks.
    // setInterval callbacks are throttled or stopped outright while the
    // page is hidden or the screen is off, so a counter under-reports
    // exactly when it matters most: the learner who wakes the phone after a
    // dark stretch would see less time than really passed.
    // performance.now() is monotonic, so a system-clock correction or a
    // learner changing the time cannot move it either.
    updateElapsedSeconds() {
      this.refreshElapsedSeconds();
      // The ceiling is checked against the clock, never against how many
      // callbacks have run: a throttled interval that fired twice in ninety
      // seconds must still stop the recording at ninety seconds.
      if (
        this.screenAwakeArmed &&
        this.captureLive &&
        this.recordingSeconds >= this.maxRecordingSeconds
      ) {
        this.holdAtCeiling();
      }
    },
    // Fire-and-forget: acquiring the lock must never block or delay the
    // recording, and useKeepAwake swallows every failure. The outcome is
    // reported once per recording.
    armScreenAwake() {
      useKeepAwake()
        .acquire()
        .then((outcome) => {
          // A recording that ended before its acquire got a turn has
          // nothing to report: the event describes a lock taken for a live
          // recording, and its outcome set has no name for this.
          if (outcome === "superseded") return;
          useRecordingEvents().trackKeepAwake({ outcome });
        })
        .catch(() => {});
    },
    releaseScreenAwake() {
      try {
        const pending = useKeepAwake().release();
        if (pending && typeof pending.catch === "function") {
          pending.catch(() => {});
        }
      } catch (err) {
        console.warn("[keep-awake] release failed:", err);
      }
    },
    /**
     * Put one string in front of a screen reader. Callers pass the already
     * translated text so the keys stay literal at their call sites, which is
     * what `npm run locales:report` scans for.
     *
     * The region is emptied first and refilled on the next tick, because a
     * live region announces a CHANGE to its contents and nothing else.
     * Assigning the same string twice produces no DOM mutation at all, so
     * without this the second recording of a session would start silently,
     * and a quiet warning that cleared and came back would never be announced
     * a second time.
     */
    announce(text) {
      this.clearAnnouncement();
      const generation = this.announcementGeneration;
      this.$nextTick(() => {
        // Anything that emptied the region in the meantime, a discard or a
        // send landing in the same tick, wins: refilling it afterwards would
        // leave a status describing a recording that is already gone.
        if (this.announcementGeneration !== generation) return;
        this.liveAnnouncement = text;
      });
    },
    clearAnnouncement() {
      this.announcementGeneration += 1;
      this.liveAnnouncement = "";
    },
    /**
     * The ceiling's two triggers both land here. Nothing is decided in this
     * method: the guard inside holdRecording is what makes the second of the
     * two a no-op, so the timeout firing a beat after the tick that already
     * held cannot hold twice, emit a second event, or overwrite the reason.
     */
    holdAtCeiling() {
      const pending = this.holdRecording("limit");
      if (pending && typeof pending.catch === "function") pending.catch(() => {});
    },
    /**
     * Every interruption signal lands here. Nothing is decided in this method
     * either: holdRecording's guard is what makes the second signal for one
     * interruption a no-op, so a WebView that fires both `visibilitychange` and
     * `appStateChange` for a single backgrounding stops the recorder once and
     * reports one event.
     *
     * @param {'hidden'|'appStateChange'|'ended'|'mute'} signal what fired.
     */
    holdFromInterruption(signal) {
      const pending = this.holdRecording("stopped", signal);
      if (pending && typeof pending.catch === "function") pending.catch(() => {});
    },
    /**
     * Start watching for the three ways capture can be taken away mid-recording.
     *
     * The scoping is deliberate and differs per signal. The document's
     * visibility and Capacitor's app state are watched on NATIVE ONLY, because
     * on a desktop browser `visibilitychange` fires with `document.hidden` true
     * for an ordinary tab switch or a window minimize, desktop Chrome keeps
     * capturing in a background tab, and the same recorder is mounted there: a
     * learner who glances at another tab mid-sentence still has a real,
     * complete recording and must not lose it. Both native signals are needed
     * because, as the comment in ChatScreen.initAppStateListener records,
     * native WebViews do not reliably fire `visibilitychange` when the app is
     * backgrounded.
     *
     * The track's own `ended` and `mute` events are watched on EVERY platform,
     * because an ended or persistently muted track means capture has already
     * stopped being real, and a pill that still says "Recording..." over it is
     * exactly the state this work exists to remove.
     */
    startInterruptionWatch(stream) {
      this.interruptionWatchArmed = true;
      this.interruptionGeneration += 1;

      const track = stream.getTracks ? stream.getTracks()[0] : null;
      if (track && typeof track.addEventListener === "function") {
        track.addEventListener("ended", this.handleTrackEnded);
        track.addEventListener("mute", this.handleTrackMute);
        track.addEventListener("unmute", this.handleTrackUnmute);
        this.interruptionTrack = track;
      }

      if (!window.Capacitor?.isNativePlatform?.()) return;
      this.visibilityHandler = this.handleVisibilityChange;
      document.addEventListener("visibilitychange", this.visibilityHandler);
      this.initInterruptionAppStateListener(this.interruptionGeneration).catch(
        () => {}
      );
    },
    /**
     * Register the Capacitor app-state listener, copying the lifecycle guard
     * from ChatScreen.initAppStateListener: the registration is asynchronous
     * (a dynamic import, then an async addListener), so the recording it
     * belongs to can be over — or the whole component gone — by the time it
     * lands. A listener kept in that case would fire into a later recording or
     * onto a destroyed instance, so it is removed immediately instead.
     */
    async initInterruptionAppStateListener(generation) {
      try {
        const { App: CapApp } = await import("@capacitor/app");
        const listener = await CapApp.addListener(
          "appStateChange",
          ({ isActive }) => {
            if (isActive) return;
            this.holdFromInterruption("appStateChange");
          }
        );
        if (
          !this.interruptionWatchArmed ||
          this.interruptionGeneration !== generation
        ) {
          listener.remove();
          return;
        }
        this.appStateListener = listener;
        // Everything above is asynchronous, and Capacitor does not replay
        // appStateChange, so an app that went into the background while the
        // import and the registration were in flight never reaches the
        // callback. On a native WebView that also does not fire
        // visibilitychange — which is the whole reason this listener exists —
        // with a track that neither ends nor mutes, nothing else would ever
        // notice, and the recorder would go on claiming to record behind a
        // locked screen. So ask once, under the same two guards, because the
        // recording may have ended during this await as well.
        try {
          const state = await CapApp.getState();
          if (
            state &&
            state.isActive === false &&
            this.interruptionWatchArmed &&
            this.interruptionGeneration === generation
          ) {
            this.holdFromInterruption("appStateChange");
          }
        } catch (error) {
          console.error("Failed to read the app state after registering:", error);
        }
      } catch (error) {
        console.error("Failed to init recording app-state listener:", error);
      }
    },
    /**
     * Remove every interruption listener. Called from stopVolumeMonitor, which
     * runs BEFORE releaseStream stops the microphone tracks — otherwise
     * stopping the track would fire the very `ended` this watches for and the
     * recorder's own teardown would hold the recording it had just finished.
     */
    stopInterruptionWatch() {
      this.interruptionWatchArmed = false;
      if (this.visibilityHandler) {
        document.removeEventListener("visibilitychange", this.visibilityHandler);
        this.visibilityHandler = null;
      }
      if (this.interruptionTrack) {
        this.interruptionTrack.removeEventListener("ended", this.handleTrackEnded);
        this.interruptionTrack.removeEventListener("mute", this.handleTrackMute);
        this.interruptionTrack.removeEventListener(
          "unmute",
          this.handleTrackUnmute
        );
        this.interruptionTrack = null;
      }
      if (this.appStateListener) {
        this.appStateListener.remove();
        this.appStateListener = null;
      }
      this.clearMuteHoldTimer();
    },
    clearMuteHoldTimer() {
      if (this.muteHoldTimeout) {
        clearTimeout(this.muteHoldTimeout);
        this.muteHoldTimeout = null;
      }
      this.muteStartedAtMonotonic = null;
      this.muteStartedAtWall = null;
    },
    handleVisibilityChange() {
      if (!document.hidden) return;
      this.holdFromInterruption("hidden");
    },
    handleTrackEnded() {
      // Irreversible by definition, so there is nothing to wait for.
      this.holdFromInterruption("ended");
    },
    handleTrackMute() {
      // One window at a time: a second mute while one is already open would
      // otherwise leave a timer behind that clearMuteHoldTimer never sees.
      if (this.muteHoldTimeout) return;
      this.muteStartedAtMonotonic = performance.now();
      this.muteStartedAtWall = Date.now();
      this.muteHoldTimeout = setTimeout(() => {
        this.muteHoldTimeout = null;
        this.muteStartedAtMonotonic = null;
        this.muteStartedAtWall = null;
        this.holdFromInterruption("mute");
      }, MUTE_HOLD_MS);
    },
    handleTrackUnmute() {
      // How long the track was actually muted is decided by the clocks, not by
      // whether the timer above got a turn. A mobile browser freezes
      // JavaScript while the tab is hidden or the screen is locked, so the
      // track can stay muted for a minute with that timeout never running; the
      // `unmute` delivered on resume would then cancel it and leave a pill
      // claiming to record over a microphone that captured nothing for the
      // whole stretch.
      //
      // The longer of the two readings wins, and each covers the other's blind
      // spot. performance.now() is monotonic, so no clock correction can move
      // it, but it may not advance while the device is suspended, and a
      // suspension is one of the cases this window exists to catch. Date.now()
      // does advance across a suspension, but a correction can move it either
      // way; a backwards jump is simply the shorter reading and is ignored,
      // and a forwards jump inside a two-second window can at worst hold a
      // recording whose audio is kept and still sendable, which is the safe
      // direction to be wrong in.
      const mutedFor = Math.max(
        this.muteStartedAtMonotonic === null
          ? 0
          : performance.now() - this.muteStartedAtMonotonic,
        this.muteStartedAtWall === null
          ? 0
          : Date.now() - this.muteStartedAtWall
      );
      this.clearMuteHoldTimer();
      if (mutedFor >= MUTE_HOLD_MS) this.holdFromInterruption("mute");
    },
    /**
     * The Android hardware back press, dispatched on `window` by App.vue as an
     * `app-back-button` CustomEvent. App.vue navigates back only if nothing
     * called preventDefault(), and dispatchEvent runs every remaining listener
     * even after one has, so the ordering below is what this handler is about.
     *
     * Both of the recorder's modals are rendered unconditionally in its
     * template, so their own Modal listeners are registered before this one and
     * always see the press first. The lifecycle is also deliberately different
     * from the interruption listeners above: this is registered in mounted()
     * and removed in beforeUnmount(), because it must still be there in the
     * held state, and stopVolumeMonitor has already run by then.
     *
     * Without it, back today silently destroys a live recording — router.back()
     * unmounts ChatScreen and the recorder with it — and would destroy a held
     * one the learner was about to send.
     */
    handleAppBackButton(event) {
      // The spec puts all of the interruption handling behind the flag, and
      // this is part of it: with the flag off, back behaves as it does today.
      if (!this.screenAwakeArmed) return;
      // A modal already closed itself on this press. Doing anything now would
      // reopen the sheet the learner just dismissed and make back inert.
      if (event.defaultPrevented) return;
      // The quiet modal is static, so its own Modal did NOT consume the press.
      // Swallow it and do nothing else: that modal parks the only copy of the
      // captured audio, and the learner has to choose "Re-record" or
      // "Send anyway". (App.vue already declines to navigate while a
      // .modal-mask is in the document, so this is a second guard rather than
      // the only one; it is kept so this behaviour does not depend on a
      // selector in App.vue.)
      if (this.showQuietModal) {
        event.preventDefault();
        return;
      }
      // A send the learner already asked for is inside its finalize await.
      // Here too captureLive is already false and the state is not 'held', and
      // letting the press through would be the worst outcome of the lot:
      // App.vue navigates, beforeUnmount abandons the capture, the send's
      // generation check fails, and the message the learner just sent is gone
      // with no error shown anywhere. Swallow the press and do nothing else,
      // because offering to discard what they just chose to send is the wrong
      // question; a second press once the send lands navigates normally.
      if (this.isSending) {
        event.preventDefault();
        return;
      }
      // Back becomes a two-step discard, exactly like the X. The condition is
      // `captureLive || held` rather than `state !== 'initial'` because the
      // quiet modal leaves state at 'recording' with capture already ended, and
      // captureLive is reliable here only because every capture-ending path,
      // cancelRecording included, clears it through finalizeCapture.
      // holdPending is the third case, and it is the one an earlier draft got
      // wrong: while a hold is inside its collect, captureLive is already false
      // and the state is not 'held' yet, and a press in that window would have
      // fallen through to App.vue's router.back() and destroyed exactly the
      // audio the hold exists to hand back.
      if (this.captureLive || this.state === "held" || this.holdPending) {
        event.preventDefault();
        // Some other component's modal is on screen. Its own listener may not
        // have run yet, because a modal that mounted after this component
        // registered its listener after ours, so defaultPrevented above cannot
        // be trusted to have caught it. Opening the discard sheet here would
        // leave one back press having changed two things: that modal closes
        // when its listener runs, and a confirmation appears underneath it. The
        // press is still consumed, so the recording is protected either way.
        // Neither of this component's own modals can be the one found here: the
        // quiet modal returned above, and the discard confirmation would have
        // consumed the press through its own Modal listener.
        if (document.querySelector(".modal-mask")) return;
        this.showCancelConfirmation();
      }
    },
    /**
     * Stop capturing and hand what was captured back to the learner, who then
     * sends or discards it exactly as they would have done themselves.
     *
     * The guard is the whole contract. `captureLive` is false unless a
     * recording is actually running, and finalizeCapture clears it
     * synchronously, so a second signal arriving after the first has started
     * finds nothing to hold. `isSending` is set synchronously by
     * sendRecording, so a send that started a moment before the ceiling wins
     * and this becomes a no-op rather than yanking the pill out from under
     * a send in flight.
     *
     * The held state is reached even when the collect throws: a learner whose
     * recorder failed to produce a blob still needs the pill to stop claiming
     * it is recording. The send from that state then hits the existing
     * "recording failed" path.
     *
     * @param {'limit'|'stopped'} reason why capture is ending.
     * @param {string|null} signal which interruption signal fired, for the
     *   telemetry, when the reason is 'stopped'. Null for the ceiling.
     */
    async holdRecording(reason, signal = null) {
      if (!this.captureLive || this.isSending) return;
      const generation = this.captureGeneration;
      this.holdReason = reason;
      // Set before the first await and cleared in the finally below, so that
      // the whole collect is covered. See the holdPending comment in data().
      this.holdPending = true;
      // The interval that normally maintains the displayed time is throttled
      // or stopped outright by the browser while the screen is off, which is
      // exactly when the backstop timer wins the race to hold. Without this
      // the held pill would show whatever the last callback managed to
      // compute rather than the length of the recording it is holding.
      this.refreshElapsedSeconds();

      try {
        await this.finalizeCapture();
      } catch (err) {
        console.error("Failed to stop recorder while holding:", err);
      } finally {
        // Nothing awaits between here and the transition to 'held' below, so
        // the protection passes straight from this flag to the state with no
        // window in which a back press sees neither.
        this.holdPending = false;
      }

      // The learner had the whole finalize await to tap send or discard, and
      // either one owns the outcome from here: both clear the capture, and
      // reviving the held pill on top of that would show a stash that is
      // already gone.
      if (this.captureGeneration !== generation || this.state !== "recording") return;
      this.state = "held";
      this.heldOutcomePending = true;
      // Emitted here rather than at the top of the method, so that a hold the
      // learner interrupted (a discard landing during the collect's await,
      // which the generation check above turns into a no-op) does not report a
      // ceiling hold that never happened. Every emitted hold now has a
      // matching held-outcome event, which is what makes the pair a funnel.
      if (reason === "limit") useRecordingEvents().trackCeilingHold();
      else if (signal) useRecordingEvents().trackInterruptionHold({ signal });
      this.announce(
        reason === "limit"
          ? this.$t("audioRecorder.limitReached")
          : this.$t("audioRecorder.stopped")
      );
    },
    /**
     * Report what became of a held recording, once. Recordings that were
     * never held report nothing, and neither does one whose send was rejected
     * by a gate: `sent` and `discarded` are the learner's two choices, and a
     * rejection is neither.
     */
    reportHeldOutcome(outcome) {
      if (!this.heldOutcomePending) return;
      this.heldOutcomePending = false;
      useRecordingEvents().trackHeldOutcome({ outcome });
    },
    showCancelConfirmation() {
      this.showCancelModal = true;
    },
    hideCancelModal() {
      this.showCancelModal = false;
    },
    startVolumeMonitor(stream) {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const audioContext = new AudioCtx();
        // iOS often hands back a suspended context even when created in a user
        // gesture; explicitly resume so the analyser actually receives data.
        if (audioContext.state === "suspended") {
          audioContext.resume().catch(() => {});
        }
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);

        this.monitorAudioContext = audioContext;
        this.monitorAnalyser = analyser;

        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        this.quietStartTime = null;
        this.hasVibrated = false;
        // quietFrames/totalFrames are reset by startRecording instead, so the
        // send gates cannot evaluate the previous recording's counters when
        // the graph below fails to build.

        const analyze = () => {
          analyser.getByteFrequencyData(dataArray);

          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) {
            const normalized = dataArray[i] / 255;
            sum += normalized * normalized;
          }
          this.volumeLevel = Math.sqrt(sum / dataArray.length);

          this.totalFrames++;
          if (this.volumeLevel < QUIET_THRESHOLD) {
            this.quietFrames++;
            if (!this.quietStartTime) {
              this.quietStartTime = Date.now();
            } else if (Date.now() - this.quietStartTime > QUIET_ALERT_MS) {
              this.showQuietWarning = true;
              if (!this.hasVibrated && navigator.vibrate) {
                navigator.vibrate(200);
                this.hasVibrated = true;
              }
            }
          } else {
            this.quietStartTime = null;
            this.hasVibrated = false;
            this.showQuietWarning = false;
          }

          this.monitorAnimFrameId = requestAnimationFrame(analyze);
        };

        this.monitorAnimFrameId = requestAnimationFrame(analyze);
      } catch (err) {
        console.error("Volume monitor failed to start:", err);
      }
    },
    stopVolumeMonitor() {
      // First, and specifically before releaseStream stops the tracks: this
      // method is called from inside finalizeCapture, whose finally releases
      // the stream, and stopping a track fires the `ended` this watch listens
      // for. Removing the listeners here is what keeps the recorder's own
      // teardown from signalling an interruption to itself.
      this.stopInterruptionWatch();
      if (this.monitorAnimFrameId) {
        cancelAnimationFrame(this.monitorAnimFrameId);
        this.monitorAnimFrameId = null;
      }
      if (this.monitorAudioContext) {
        this.monitorAudioContext.close().catch(() => {});
        this.monitorAudioContext = null;
      }
      this.monitorAnalyser = null;
      this.volumeLevel = 0;
      this.showQuietWarning = false;
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
      // Cleared here rather than only on the hold: a recording that was sent
      // or discarded at twenty seconds must not leave a timer that fires the
      // ceiling seventy seconds later, into whatever recording is running by
      // then.
      if (this.ceilingTimeout) {
        clearTimeout(this.ceilingTimeout);
        this.ceilingTimeout = null;
      }
      // Unconditional, and deliberately not gated on screenAwakeArmed: a
      // release that is skipped leaves the OS flag set, and on Android that
      // pins the screen again on the next resume.
      this.releaseScreenAwake();
    },
    releaseStream() {
      if (this.stream) {
        this.stream.getTracks().forEach((t) => t.stop());
        this.stream = null;
      }
    },
    async stopRecorderAndCollect() {
      if (!this.recorder) return null;
      const recorder = this.recorder;
      const chunks = this.recordedChunks;
      const mimeType = recorder.mimeType || pickMimeType() || "audio/webm";

      // Only wait on the stop event if we actually need to call stop().
      // If the recorder is already inactive (e.g. after a MediaRecorder
      // error event), the stop event won't fire and an unconditional await
      // would hang forever. Likewise, resolve if stop() throws synchronously
      // or if the stop event simply never arrives (WebKit has had bugs where
      // it doesn't), so a stuck recorder can't deadlock the UI.
      if (recorder.state !== "inactive") {
        await new Promise((resolve) => {
          let resolved = false;
          const finish = () => {
            if (resolved) return;
            resolved = true;
            resolve();
          };
          recorder.addEventListener("stop", finish, { once: true });
          const timeoutId = setTimeout(() => {
            console.error("MediaRecorder.stop event timed out after 3s");
            finish();
          }, 3000);
          try {
            recorder.stop();
          } catch (e) {
            console.error("Recorder stop threw:", e);
            clearTimeout(timeoutId);
            finish();
          }
        });
      }

      this.recorder = null;
      this.recordedChunks = [];
      const blob = new Blob(chunks, { type: mimeType });
      return { blob, mimeType };
    },
    /**
     * The one way capture ends. Every path that stops a recording goes
     * through it: send, discard, and unmount.
     *
     * Memoized on the IN-FLIGHT PROMISE, not on the result. That is what
     * makes it safe to call from two signals at once. Memoizing the
     * resolved stash would not be: a second call arriving during
     * stopRecorderAndCollect's three-second await would see the recorder
     * still non-null but already inactive, reset recordedChunks before the
     * final dataavailable chunk landed, and build a second blob missing the
     * last second of audio.
     *
     * Everything before the first await runs synchronously, so a caller
     * that does not await it (beforeUnmount) still gets captureLive cleared
     * and every timer the recorder owns torn down.
     *
     * @returns {Promise<{blob: Blob|null, mimeType: string,
     *   quietRatio: number, totalFrames: number}>} rejects if the collect
     *   throws, in which case no stash is written.
     */
    finalizeCapture() {
      if (this.finalizePromise) return this.finalizePromise;

      this.finalizePromise = (async () => {
        try {
          this.captureLive = false;
          // Snapshot before stopping the meter: the live counters stop
          // advancing the moment the animation frame loop is cancelled
          // below, and a held recording is judged by these numbers even if
          // the live ones move afterwards.
          const totalFrames = this.totalFrames;
          const quietRatio =
            totalFrames > 0 ? this.quietFrames / totalFrames : 0;
          this.stopVolumeMonitor();
          const result = await this.stopRecorderAndCollect();
          const payload = {
            blob: result ? result.blob : null,
            mimeType: result ? result.mimeType : "",
            quietRatio,
            totalFrames,
          };
          this.capturedPayload = payload;
          return payload;
        } finally {
          this.releaseStream();
        }
      })();

      return this.finalizePromise;
    },
    /**
     * Drop the memoized promise and the stash. Called at the start of every
     * recording and on every terminal outcome. Without it the second
     * recording of a session would short-circuit on the first recording's
     * promise, never stop its own recorder (leaving the microphone open),
     * and send the first recording's audio again.
     */
    /**
     * Say, synchronously, that whatever capture is in progress has been
     * abandoned. Timing is the whole point: a send that is already awaiting
     * the memoized finalize promise resumes BEFORE the discard path's own
     * finally block runs, because it registered its continuation first. A
     * discard that only cleaned up in its finally would therefore lose the
     * race and the send would emit audio the learner had just thrown away.
     */
    abandonCapture() {
      this.captureGeneration += 1;
    },
    clearCapture() {
      this.finalizePromise = null;
      this.capturedPayload = null;
      // The stash and the held bookkeeping go together: with the stash gone
      // there is nothing left to send or discard, so an outcome reported
      // after this point would describe a recording that no longer exists.
      // Every caller that owes an outcome event reports it BEFORE calling in
      // here.
      this.heldOutcomePending = false;
      this.holdReason = null;
      this.holdPending = false;
      // The pill is going away, so the last status it announced is stale. A
      // screen-reader user navigating back over the region afterwards should
      // not find "Recording stopped" still sitting in it. This also cancels an
      // announcement that is mid-flight in announce()'s nextTick callback.
      this.clearAnnouncement();
      this.abandonCapture();
    },
    async cancelRecording() {
      this.abandonCapture();
      try {
        await this.finalizeCapture();
      } catch (err) {
        // The collect can reject; the finally below is what keeps the
        // learner from being stranded in a dead "recording" pill when it
        // does.
        console.error("Failed to stop recorder while discarding:", err);
      } finally {
        this.reportHeldOutcome("discarded");
        this.clearCapture();
        this.pendingPayload = null;
        this.state = "initial";
        this.showCancelModal = false;
      }
    },
    async sendRecording() {
      // Guard against double-click on the send button: finalizeCapture is
      // async so without this a second click would race with the first,
      // emitting the same blob twice.
      if (this.isSending) return;
      this.isSending = true;
      const generation = this.captureGeneration;
      // The learner starts waiting HERE, at the tap — not when the emit below
      // finally fires. finalizeCapture is awaited in between, and its
      // stop-event fallback can take seconds, so a reply-lag anchor armed at
      // the emit would silently omit that much of the wait from a metric whose
      // whole target is measured in seconds. Carried on the payload rather
      // than read from the clock downstream for exactly that reason.
      const tapAtMs = Date.now();

      let captured;
      try {
        captured = await this.finalizeCapture();
      } catch (err) {
        console.error("Failed to stop recorder:", err);
        useToast().showToast(this.$t("audioRecorder.recordingFailed"), "error");
        this.clearCapture();
        this.state = "initial";
        return;
      } finally {
        this.isSending = false;
      }

      // The cancel button stays live for the whole finalize await, and a
      // discard joins the same memoized promise this send is waiting on. If
      // it got there first it has already cleared the capture, and sending
      // anyway would ship audio the learner explicitly threw away.
      if (this.captureGeneration !== generation) return;

      // The gates below read the SNAPSHOT taken when capture ended, not the
      // live counters: a recording can sit stashed (quiet modal, and later a
      // held recording) long after the meter stopped.
      const { blob, mimeType, quietRatio, totalFrames } = captured || {};

      if (!blob) {
        useToast().showToast(this.$t("audioRecorder.recordingFailed"), "error");
        this.clearCapture();
        this.state = "initial";
        return;
      }

      if (blob.size < MIN_BLOB_SIZE_BYTES) {
        console.error("Empty or too-small audio blob:", blob.size, mimeType);
        useToast().showToast(this.$t("audioRecorder.noAudioCaptured"), "error");
        this.clearCapture();
        this.state = "initial";
        return;
      }

      // Nothing on the upload path bounded the body before this: ChatScreen
      // builds the multipart request with no size check, and continueChat
      // read the file straight into a 1 GB-memory function. A blob this large
      // is a malformed capture rather than a long recording, so it gets the
      // existing failure toast instead of copy of its own. The unflagged
      // check runs whatever the ceiling flag says, because an unbounded
      // recording is exactly the case that can reach this size.
      if (blob.size > MAX_BLOB_BYTES) {
        console.error(
          "Recording exceeds the upload bound",
          { blobSize: blob.size, bound: MAX_BLOB_BYTES }
        );
        useToast().showToast(this.$t("audioRecorder.recordingFailed"), "error");
        this.clearCapture();
        this.state = "initial";
        return;
      }

      // The volume-monitor RMS is the most reliable signal we have that real
      // audio actually reached us. If almost every frame was below the quiet
      // threshold, the captured stream is broken (silent mic, simulator
      // returning a dead stream, iOS WKWebView session contention, etc.) —
      // refuse to send it and tell the user.
      if (totalFrames >= 30 && quietRatio >= SILENT_RATIO_FAIL) {
        console.error(
          "Recording was silent",
          { quietRatio, totalFrames, blobSize: blob.size }
        );
        useToast().showToast(this.$t("audioRecorder.noAudioCaptured"), "error");
        this.clearCapture();
        this.state = "initial";
        return;
      }

      const payload = {
        blob,
        mimeType,
        extension: extensionFor(mimeType),
        tapAtMs,
      };

      if (quietRatio > SILENT_RATIO_WARN) {
        this.pendingPayload = payload;
        this.showQuietModal = true;
        return;
      }

      if (this._hapticMedium) this._hapticMedium();
      this.reportHeldOutcome("sent");
      this.$emit("new-recording", payload);
      this.clearCapture();
      this.state = "initial";
    },
    confirmSendQuiet() {
      if (this.pendingPayload) {
        this.reportHeldOutcome("sent");
        // Re-stamped, not carried over from sendRecording: the quiet-recording
        // modal is the learner deciding whether to send at all, and however
        // long they spent on that decision is not time they spent waiting for
        // a reply.
        this.$emit("new-recording", { ...this.pendingPayload, tapAtMs: Date.now() });
      }
      this.pendingPayload = null;
      this.clearCapture();
      this.showQuietModal = false;
      this.state = "initial";
    },
    discardQuietRecording() {
      this.pendingPayload = null;
      this.reportHeldOutcome("discarded");
      this.clearCapture();
      this.showQuietModal = false;
      this.state = "initial";
    },
    getBarHeight(barIndex) {
      const minHeight = 3;
      const maxHeight = 18;
      const offsets = [0.6, 0.8, 1.0, 0.85, 0.65];
      const scaled = this.volumeLevel * offsets[barIndex - 1] * maxHeight;
      return Math.max(minHeight, Math.min(maxHeight, scaled));
    },
  },
  beforeUnmount() {
    this.isUnmounted = true;
    // The take ends with this instance, whatever state the teardown below
    // leaves it in; the watcher that would otherwise report it stops here.
    setTakeInProgress(this.recordingHost, false);
    // Removed here rather than in stopVolumeMonitor: the back press has to
    // stay handled in the held state, which is reached only after
    // stopVolumeMonitor has already run.
    window.removeEventListener("app-back-button", this.handleAppBackButton);
    this.abandonCapture();
    // Not awaited: Vue does not wait for an async hook. Everything this
    // instance must not leave behind — captureLive, the meter, the timer
    // and the wake lock — is torn down synchronously inside
    // finalizeCapture before its first await, and the stream is released in
    // its finally. Without going through finalizeCapture, captureLive would
    // stay stuck true and a surviving timer could re-enter it on a
    // torn-down instance.
    // The stash is cleared once the teardown has actually settled, not on
    // the next line. Nulling the memoized promise synchronously would let a
    // late caller start a SECOND teardown against a half-stopped recorder,
    // and the in-flight one would write the stash back onto this dead
    // instance a moment later anyway. Both handlers are supplied so a
    // rejecting collect is swallowed rather than surfacing as an unhandled
    // rejection from a component nobody is watching.
    this.finalizeCapture().then(
      () => this.clearCapture(),
      () => this.clearCapture()
    );
    // Hand the microphone back NOW rather than on the collect's finally. That
    // finally is on the far side of an await which runs for up to three
    // seconds when a WebView never fires the stop event, and for those three
    // seconds the mic and the OS recording indicator would stay live on a
    // screen the learner has already navigated away from. Nothing is lost:
    // recorder.stop() was already issued synchronously by the call above, and
    // an unmount discards the audio regardless. releaseStream is idempotent,
    // so the finally becomes a no-op.
    this.releaseStream();
    this.pendingPayload = null;
  },
  mounted() {
    const { impactMedium } = useHaptics();
    this._hapticMedium = impactMedium;
    // Both of this component's modals register their own listener for the same
    // event in their mounted(), and a child's mounted() runs before its
    // parent's, so their listeners always see the press before this one.
    window.addEventListener("app-back-button", this.handleAppBackButton);
  },
};
</script>

<style scoped>
.voice-recorder-container {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  /* No safe-area padding here: the chat footer (ChatScreen .card-footer)
     already accounts for the bottom tab bar and safe-area inset. */
}

/* Idle mic — the screen's primary action. DESIGN.md assigns primary blue
   (#2D90EB) to "recording/listening focus", and elevation must be soft
   blue/navy shadows, never black. */
.recorder-button {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: none;
  background-color: var(--al-primary);
  color: var(--al-white);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color var(--al-transition), transform var(--al-transition), box-shadow var(--al-transition);
  box-shadow: 0 6px 18px rgba(45, 144, 235, 0.35);
}

.recorder-button:hover:not(:disabled) {
  background-color: var(--al-primary-hover);
  transform: translateY(-1px) scale(1.03);
  box-shadow: 0 8px 22px rgba(45, 144, 235, 0.42);
}

.recorder-button:active:not(:disabled) {
  transform: scale(0.96);
  box-shadow: 0 3px 10px rgba(45, 144, 235, 0.3);
}

.recorder-button:focus-visible {
  outline: 2px solid var(--al-primary);
  outline-offset: 3px;
}

.recorder-button:disabled {
  background-color: var(--al-surface-sunken);
  box-shadow: none;
  cursor: not-allowed;
}

.recorder-button:disabled .mic-icon {
  color: var(--al-gray-400);
}

.mic-icon {
  font-size: 24px;
  color: inherit;
}

/* Recording state: a calm raised pill. Live volume bars carry the "this is
   recording" signal in primary blue (DESIGN.md: listening states should feel
   calm and explicit). */
.recording-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
  background-color: var(--al-surface-raised);
  border: 1px solid var(--al-border);
  padding: 6px;
  border-radius: var(--al-radius-pill);
  box-shadow: var(--al-shadow-sm);
  animation: slideIn var(--al-dur-medium) var(--al-ease-enter);
}

/* Mobile overrides use the `.voice-recorder-container` prefix for higher
   specificity: the production CSS minifier reorders equal-specificity
   duplicate selectors, so media-block rules must win by specificity, not
   source order. No dppx-based shrinking: it used to squash the controls
   below the 44px touch-target floor on every modern phone (~3dppx). */
@media (max-width: 767px) {
  .voice-recorder-container .recorder-button {
    width: 56px;
    height: 56px;
  }

  .voice-recorder-container .recording-controls {
    gap: 0.5rem;
    width: 100%;
    justify-content: space-between;
  }

  .voice-recorder-container .recording-indicator {
    padding: 0 0.25rem;
    gap: 0.35rem;
    flex: 1;
    min-width: 0;
    justify-content: center;
  }

  .voice-recorder-container .recording-text {
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 110px;
  }

  /* 110px was sized for "Recording..." and "Speak louder". The countdown and
     the two held labels run longer, and in the held state the volume bars are
     gone, so the label may take the space they were using. Written as a
     two-class selector so it beats the rule above on specificity rather than
     on source order: the production CSS minifier reorders equal-specificity
     duplicate selectors. */
  .voice-recorder-container .recording-text.recording-text-roomy {
    max-width: 100%;
  }
}

.control-button {
  width: 48px;
  height: 48px;
  min-width: 48px;
  aspect-ratio: 1;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color var(--al-transition), color var(--al-transition), border-color var(--al-transition), transform var(--al-transition);
  flex-shrink: 0;
  font-size: 18px;
}

/* Quiet dismiss control: destructive red is reserved for the confirm modal's
   actual discard action; here it competes with send otherwise. */
.cancel-button {
  background-color: transparent;
  color: var(--al-text-muted);
  border: 1px solid var(--al-border);
}

.cancel-button:hover {
  color: var(--al-danger);
  border-color: var(--al-danger-border);
  background-color: var(--al-danger-bg);
}

.send-button {
  background-color: var(--al-primary);
  color: var(--al-white);
  box-shadow: 0 4px 12px rgba(45, 144, 235, 0.35);
}

.send-button:hover {
  background-color: var(--al-primary-hover);
  transform: scale(1.05);
}

/* Recording indicator styles */
.recording-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 1rem;
}

/* Volume bars */
.volume-bars {
  display: flex;
  align-items: center;
  gap: 3px;
  height: 20px;
}

.volume-bar {
  width: 3px;
  min-height: 3px;
  background-color: var(--al-primary);
  border-radius: 1.5px;
  transition: height 0.1s ease, background-color var(--al-dur-medium) var(--al-ease-move);
}

.volume-quiet .volume-bar {
  background-color: var(--al-amber);
}

.recording-timer {
  font-size: 14px;
  font-weight: 600;
  color: var(--al-text);
  font-variant-numeric: tabular-nums;
  min-width: 40px;
  text-align: center;
}

.recording-text {
  font-size: 14px;
  color: var(--al-text-muted);
  /* A flex item's automatic minimum size is its content width, so without
     this the mobile max-width clip never engages: `overflow: hidden` has
     nothing to hide against, and a long German or Russian label spills over
     the send and cancel buttons, which are flex-shrink: 0. */
  min-width: 0;
}

/* Quiet warning text: warning-text token keeps AA contrast on the raised
   surface in both themes (raw amber is ~2.2:1 on white). */
.recording-text-warning {
  color: var(--al-warning-text);
  font-weight: 600;
}

@keyframes slideIn {
  from {
    transform: translateX(-10px);
    opacity: 0;
  }

  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .recording-controls {
    animation: none;
  }
}

/* Confirmation sheets: icon badge + title + supporting text + stacked
   full-width actions (DESIGN.md empty-state / button specs), replacing the
   old header-plus-flat-buttons alert look. Scoped styles reach this markup
   because slot content keeps the parent's scope id through the Teleport. */
.confirm-sheet {
  text-align: center;
  padding: var(--al-space-sm) var(--al-space-sm) var(--al-space-md);
}

.confirm-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--al-space-md);
  font-size: 22px;
}

.confirm-icon.danger {
  background-color: var(--al-danger-bg);
  color: var(--al-danger);
}

.confirm-icon.warning {
  background-color: var(--al-warning-bg);
  color: var(--al-warning-text);
}

/* H3 from the DESIGN.md type scale: 20px / 1.24 / 700. */
.confirm-title {
  font-size: 20px;
  line-height: 1.24;
  font-weight: 700;
  color: var(--al-text);
  margin: 0 0 var(--al-space-sm);
}

.confirm-text {
  font-size: 15px;
  line-height: 1.45;
  color: var(--al-text-muted);
  margin: 0 0 var(--al-space-lg);
}

.confirm-actions {
  display: flex;
  flex-direction: column;
  gap: var(--al-space-sm);
}

.confirm-btn {
  width: 100%;
  min-height: 48px;
  padding: 12px 16px;
  border-radius: var(--al-radius-md);
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color var(--al-transition), color var(--al-transition), border-color var(--al-transition);
}

.confirm-btn.primary {
  background-color: var(--al-primary);
  border: none;
  color: var(--al-white);
}

.confirm-btn.primary:hover {
  background-color: var(--al-primary-hover);
}

.confirm-btn.secondary {
  background-color: transparent;
  border: 1px solid var(--al-border);
  color: var(--al-text);
}

.confirm-btn.secondary:hover {
  background-color: var(--al-surface-sunken);
}

/* Destructive secondary: danger-text token stays AA on the raised surface in
   both themes (raw --al-danger is only ~3.4:1 on the dark surface). */
.confirm-btn.destructive {
  background-color: transparent;
  border: 1px solid var(--al-border);
  color: var(--al-danger-text);
}

.confirm-btn.destructive:hover {
  background-color: var(--al-danger-bg);
  border-color: var(--al-danger-border);
}
</style>

<!-- Unscoped on purpose: .confirm-dialog is applied inside Modal.vue's own
     template via the dialogClass prop, so this component's scope id never
     reaches it. Narrows the desktop dialog so the stacked buttons read as a
     compact confirmation card instead of a stretched 500px alert. -->
<style>
@media (min-width: 769px) {
  .modal .confirm-dialog {
    width: 400px;
    max-width: 400px;
  }
}
</style>
